import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import orderBy from 'lodash/orderBy';
import ReactPaginate from 'react-paginate';
import { withRouter } from 'react-router-dom';

import { HeaderRow, Row, NoItemsFound, TableLoader } from '.';

const DEFAULT_PAGE_NUMBER = 0;
const DEFAULT_PAGE_SIZE = 25;

const parseUrl = (search, field, fieldValue) => {
  const urlParams = new URLSearchParams(search);

  urlParams.set(field, fieldValue);

  return urlParams.toString();
};

class EnhancedTable extends PureComponent {
  state = {
    page: DEFAULT_PAGE_NUMBER,
    size: DEFAULT_PAGE_SIZE,
    sort: '',
    filterValue: '',
    filterFields: '',
  };

  componentDidMount() {
    const {
      config: {
        initialSort: { sortField, sortDirection },
        // initialFilters,
        filterFields = '',
      },
      params,
      location: { search },
    } = this.props;

    const urlParams = new URLSearchParams(search);

    let { page } = this.state;

    if (urlParams.get('page')) {
      page = Number.parseInt(urlParams.get('page'), 0);
    }

    let { filterValue } = this.state;

    if (urlParams.get('filterValue')) {
      filterValue = urlParams.get('filterValue');
    }

    let sort = `${sortField},${sortDirection}`;
    if (urlParams.get('sort')) {
      sort = urlParams.get('sort');
    }

    this.setState(
      {
        sort,
        ...params,
        page,
        filterFields,
        filterValue,
      },
      this.callFetchData,
    );
  }

  handleSort = (name, direction) => {
    const {
      history,
      location: { search },
    } = this.props;

    const sort = `${name},${direction}`;
    const url = parseUrl(search, 'sort', sort);

    history.push(`?${url}`);

    this.setState({ sort }, this.callFetchData);
  };

  handleChangePage = ({ selected }) => {
    const {
      history,
      location: { search },
    } = this.props;

    const url = parseUrl(search, 'page', selected);

    history.push(`?${url}`);

    this.setState({ page: selected }, this.callFetchData);
  };

  callFetchData = () => {
    const { fetchData } = this.props;

    if (fetchData) {
      fetchData({ ...this.state });
    }
  };

  sortItems = (items) => {
    const { sort } = this.state;
    const [name, direction] = sort.split(',');

    return orderBy(items, [name], [direction]);
  };

  setFilters = (ev) => {
    const {
      history,
      location: { search },
    } = this.props;
    const {
      currentTarget: { value },
    } = ev;

    const url = parseUrl(search, 'filterValue', value);

    history.push(`?${url}`);

    this.setState({ filterValue: value }, this.callFetchData);
  };

  render() {
    const {
      className,
      config: { columns, noItemsFoundText, idProperty = 'id' },
      isLoading,
      items,
      onRowClick,
      pagination,
      totalCount,
      theme,
      noBorders,
    } = this.props;
    const { size, page, filterValue } = this.state;
    const sortedItems = this.sortItems(items);

    return (
      <div className={`card ${noBorders ? 'border-0' : ''}`}>
        <div className={`card-header  ${noBorders ? 'border-0 ' : ''}`}>
          <div className="input-group">
            <div className="input-group-append">
              <div className="input-group-text">
                <i className="fa fa-search" />
              </div>
            </div>
            <input
              type="text"
              value={filterValue}
              onChange={this.setFilters}
              placeholder="Szukaj.."
              className="form-control"
            />
          </div>
        </div>
        <div className="card-body">
          <table className={className}>
            <thead>
              <HeaderRow
                config={columns}
                handleSort={this.handleSort}
                theme={theme}
              />
            </thead>
            {isLoading ? (
              <TableLoader columns={columns.length} />
            ) : (
              <tbody>
                {sortedItems.map((item) => (
                  <Row
                    key={item[idProperty]}
                    item={item}
                    onRowClick={onRowClick}
                    config={columns}
                    idProperty={idProperty}
                  />
                ))}
                {sortedItems.length === 0 && (
                  <NoItemsFound
                    columnsLength={columns.length}
                    text={noItemsFoundText}
                  />
                )}
              </tbody>
            )}
            {pagination && (
              <tfoot>
                <tr>
                  <td colSpan={columns.length}>
                    <ReactPaginate
                      disableInitialCallback
                      forcePage={page}
                      previousLabel="Poprzednie"
                      nextLabel="Następne"
                      breakLabel="..."
                      breakClassName="break-me"
                      pageCount={(totalCount / size) * 1}
                      marginPagesDisplayed={2}
                      pageRangeDisplayed={5}
                      onPageChange={this.handleChangePage}
                      containerClassName="pagination float-right"
                      pageClassName="page-item"
                      pageLinkClassName="page-link"
                      activeClassName="active"
                      nextClassName="page-item"
                      previousClassName="page-item"
                      nextLinkClassName="page-link"
                      previousLinkClassName="page-link"
                    />
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    );
  }
}

EnhancedTable.propTypes = {
  className: PropTypes.string,
  config: PropTypes.shape({
    idProperty: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.shape),
    noItemsFoundText: PropTypes.string,
    initialSort: PropTypes.shape({
      sortField: PropTypes.string,
      sortDirection: PropTypes.string,
    }),
  }).isRequired,
  fetchData: PropTypes.func,
  isLoading: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape).isRequired,
  onRowClick: PropTypes.func,
  pagination: PropTypes.bool,
  totalCount: PropTypes.number,
  params: PropTypes.shape({}),
  theme: PropTypes.string,
  noBorders: PropTypes.bool,
  history: ReactRouterPropTypes.history.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
};

EnhancedTable.defaultProps = {
  className: 'table',
  fetchData: null,
  isLoading: false,
  onRowClick: () => {},
  pagination: false,
  totalCount: 0,
  params: {},
  theme: 'light',
  noBorders: false,
};

export default withRouter(EnhancedTable);
